import { components, useApi } from "@appretio/api";
import { useQuery } from "@tanstack/react-query";
import { storeStatusQueryKeys } from "./queryKeys";

export type StoreStatusOption = components["schemas"]["StoreStatusOption"];

/**
 * Fetches store status options with their associated sales_locations.
 * Used by the StoreStatusFilter to resolve status selections into locations.
 */
export function useFetchStoreStatusOptions() {
  const client = useApi();

  return useQuery({
    queryKey: storeStatusQueryKeys.all,
    meta: {
      errorMessage: "Failed to fetch store status options"
    },
    queryFn: async (): Promise<StoreStatusOption[]> => {
      const response = await client.GET("/v2/stores/status-options");

      return response.data?.result?.options ?? [];
    },
    staleTime: Infinity
  });
}

/**
 * Resolves selected store statuses to a set of sales_location values.
 */
export function resolveStoreStatusToLocations(
  selectedStatuses: string[],
  options: StoreStatusOption[]
): string[] {
  if (selectedStatuses.length === 0) return [];

  const statusSet = new Set(selectedStatuses);
  return options
    .filter((opt) => statusSet.has(opt.status))
    .flatMap((opt) => opt.sales_locations);
}

/**
 * Resolves selected sales locations to the set of store statuses that
 * contain at least one of those locations (reverse cross-filter).
 */
export function resolveLocationsToStoreStatuses(
  selectedLocations: string[],
  options: StoreStatusOption[]
): string[] {
  if (selectedLocations.length === 0) return [];

  const locationSet = new Set(selectedLocations);
  return options
    .filter((opt) => opt.sales_locations.some((loc) => locationSet.has(loc)))
    .map((opt) => opt.status);
}
